import axios from 'axios';
import { mutate } from 'swr';
import { Notistack } from '@shared/components/Notistack/Notistack';
import { SERVICES } from '@shared/constants/apiConfig';
import { getError } from '@shared/utils/helpers';
import { IPullPayOffQuoteResponse } from '@modules/ContactManager/components/Tabs/interfaces';
import { withQueryParams } from '@shared/commonFunctions';
import { IDealSheetAdjustment, IPayoffStatus } from '../../interfaces';
import { IAmorizationDetailer, ICancelPayOffQuoteResponse, ICommentRequest, IPayoffQuote, ISubmitPayOffQuoteResponse, IntrestRateCalc} from './interfaces';

const path = `${SERVICES.DEAL}/lendercomment`;

export const sendLenderCommnet = (data: ICommentRequest) => {
  return mutate(`${path}/deals/${data.dealId}/lendercomments-all`, async() => {
    try {
      const response = await axios.post(path + '/receivelendercomments', data);
      Notistack.successMessage('Calculations done successfully');
      return response.data;
    } catch (err) {
      const error = err as { response: { data: { message: string } } };
      Notistack.generalError(error?.response?.data?.message);
    }
  });
};

export const requestPullFinalPayoffQuote = (dealId: string): Promise<IPullPayOffQuoteResponse | undefined> => {
  const url = `${SERVICES.ASSET}/v2/lienholder/${dealId}/pull-final-payOff-quote`;
  return mutate<IPullPayOffQuoteResponse>(url, async() => {
    try {
      const response = await axios.post(url);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestSubmitPayoffQuote = (dealId: string, refundAmount?: number | null): Promise<ISubmitPayOffQuoteResponse | undefined> => {
  const url = `${SERVICES.ASSET}/v2/lienholder/${dealId}/submit-payOff?refundAmount=${refundAmount ? refundAmount : 0}`;
  return mutate<ISubmitPayOffQuoteResponse>(url, async () => {
    try {
      const response = await axios.post(url);
      Notistack.successMessage('PayOff submitted successfully');

      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestUpdateAssetLienholder = (contactId: string, assetId: string): Promise<void> => {
  const url = `${SERVICES.ASSET}/contact/${contactId}/assets/${assetId}/accelerated-title-not-supported`;
  return mutate<void>(url, async() => {
    try {
      await axios.patch(url);
      Notistack.successMessage('Updated asset lienholder.');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const requestCancelPayoff = (dealId: string) => {
  const url = `${SERVICES.ASSET}/v2/lienholder/${dealId}/payoffs/cancel-payOff`;

  return mutate<ICancelPayOffQuoteResponse>(url, async() => {
    try {
      const response = await axios.post(url);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};


export const requestPayoffStatus = (dealId: string): Promise<IPayoffStatus | undefined> => {
  const url = `${SERVICES.ASSET}/v2/lienholder/${dealId}/payoffs`;
  return mutate<IPayoffStatus>(url, async() => {
    try {
      const response = await axios.get(url);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  }); 
};

export const createDealSheetAdjustment = (data: IDealSheetAdjustment) => {
  const url = `${SERVICES.ACCOUNTING}/accounting/dealsheetadjustments/create`;
  return mutate(url, async() => {
    try {
      const response = await axios.post<IDealSheetAdjustment>(url, data);
      return response.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const savePayoffQuote = (data: IPayoffQuote, contactId?: string, assetId?: string) => {
  const url = `${SERVICES.ASSET}/contacts/${contactId}/assets/${assetId}/payoff`;

  return mutate(url, async () => {
    try {
      await axios.patch(url, {...data });
      Notistack.successMessage('Payoff Quote Saved successfully');
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const calculateInrestRateNotApr = (data: IntrestRateCalc) => {
  const url = withQueryParams(`/${SERVICES.DEAL}/calculateapr`, { ...data }, false);

  return mutate(url, async () => {
    try {
      const resp = await axios.get(url);
      Notistack.successMessage('Calculation Done Successfully');

      return resp.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};

export const calculateAmorizationDetailer = (data: IAmorizationDetailer) => {
  const url = withQueryParams(`/${SERVICES.DEAL}/amortization/calculator`, { ...data }, false);

  return mutate(url, async () => {
    try {
      const resp = await axios.get(url);
      Notistack.successMessage('Calculation Done Successfully');

      return resp.data;
    } catch (err) {
      Notistack.generalError(getError(err));
    }
  });
};